// Source-agnostic discount stepping: ordering and progress math over
// `NormalizedDiscountData`, shared by the cart and the photo styler. Text lives
// in `discountStepsText.ts`.

export type DiscountGoal = 'shipping' | 'buyover' | 'bundle' | 'percent'

// `target`/`remaining` in the goal's natural unit (currency for shipping/buyover,
// tile count for bundle), so the progress math is unit-agnostic.
type SpendGoalFacts = {
  target: number
  remaining: number
  applied: boolean
}

export type GoalFacts =
  | ({ goal: 'shipping' } & SpendGoalFacts)
  | ({ goal: 'buyover' } & SpendGoalFacts & {
        discountPercent: number
        totalSaved: number
        isHighestTier: boolean
      })
  // A bundle repeats, so one goal covers a run of them.
  | ({ goal: 'bundle' } & SpendGoalFacts & {
        singleBundleTilesCount: number
        singleBundlePrice: number
        appliedBundlesCount: number
      })
  // A flat percentage is always applied.
  | {
      goal: 'percent'
      applied: true
      percentOff: number
      isHighestTier?: boolean
    }

// Tiers don't stack: a tier below the ladder's highest reached one has its
// discount replaced, so its applied copy is dropped.
export function isSupersededTier(goal: GoalFacts): boolean {
  return (
    (goal.goal === 'buyover' || goal.goal === 'percent') &&
    goal.isHighestTier === false
  )
}

export type NormalizedDiscountData = {
  currency: string
  shipping?: SpendGoalFacts
  mainGoals?: GoalFacts[]
}

export type DiscountStep = GoalFacts & { progress: number }

function clampProgress(value: number): number {
  return Math.min(Math.max(value, 0), 100)
}

export function progressFor(goal: GoalFacts): number {
  if (goal.goal === 'percent') return 100
  if (goal.target <= 0) return 0
  const target =
    goal.goal === 'bundle' ? goal.singleBundleTilesCount : goal.target
  return clampProgress(((target - goal.remaining) / target) * 100)
}

// The spend (in currency) at which the goal unlocks; ordering goals by it puts a
// goal achievable before shipping to its left. `percent` always applies.
function getUnlockThreshold(goal: GoalFacts): number {
  switch (goal.goal) {
    case 'percent':
      return 0
    case 'bundle':
      return goal.singleBundlePrice * goal.appliedBundlesCount
    case 'shipping':
    case 'buyover':
      return goal.target
  }
}

export function buildDiscountSteps(
  data: NormalizedDiscountData
): DiscountStep[] {
  const goals: GoalFacts[] = []
  if (data.shipping) {
    goals.push({ goal: 'shipping', ...data.shipping })
  }
  if (data.mainGoals) {
    goals.push(...data.mainGoals)
  }
  return goals
    .sort((a, b) => getUnlockThreshold(a) - getUnlockThreshold(b))
    .map((goal) => ({ ...goal, progress: progressFor(goal) }))
}

// The first not-yet-reached goal.
export function getActiveStep(steps: DiscountStep[]): DiscountStep | undefined {
  return steps.find((step) => !step.applied)
}

export function isAllReached(steps: DiscountStep[]): boolean {
  return steps.length > 0 && steps.every((step) => step.applied)
}
